import React, { useState } from 'react';
import { formatPhoneNumber, getFullPhoneE164 } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Phone,
  MessageCircle,
  Briefcase,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Clock,
  PhoneOff,
  XCircle,
  StickyNote,
  AlertCircle,
} from 'lucide-react';
import { formatDistanceToNow, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { useTwilio } from '@/contexts/TwilioContext';
import { OutboundCallHistory } from './OutboundCallHistory';
import type { OutboundContact, OutboundStatus } from '@/hooks/useOutboundQueue';
import DealForm from '@/components/deals/DealForm';
import { useDeals } from '@/hooks/useDeals';
import { ChatCore, ContactChatHeader } from '@/features/chat';

const STATUS_LABELS: Record<OutboundStatus, string> = {
  pending: 'Pendente',
  called_no_answer: 'Sem resposta',
  called_not_interested: 'Não quis',
  deal_created: 'Deal criado',
};

const STATUS_COLORS: Record<OutboundStatus, string> = {
  pending: 'bg-slate-100 text-slate-700',
  called_no_answer: 'bg-yellow-100 text-yellow-800',
  called_not_interested: 'bg-red-100 text-red-700',
  deal_created: 'bg-green-100 text-green-700',
};

interface OutboundContactCardProps {
  item: OutboundContact;
  onStatusChange: (assignmentId: string, sdrId: string, status: OutboundStatus, incrementAttempts?: boolean) => void;
  onNoteSave: (assignmentId: string, sdrId: string, notes: string) => void;
  isUpdating?: boolean;
}

export function OutboundContactCard({
  item,
  onStatusChange,
  onNoteSave,
  isUpdating,
}: OutboundContactCardProps) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesValue, setNotesValue] = useState(item.notes ?? '');
  const [showDealForm, setShowDealForm] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const { makeCall } = useTwilio();
  const { createDeal } = useDeals({ enabled: false });

  const { contact } = item;
  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || '—';
  const phoneFormatted = contact.primary_phone
    ? formatPhoneNumber(contact.primary_phone, contact.primary_phone_country_code || '+1')
    : null;
  const phoneE164 = contact.primary_phone
    ? getFullPhoneE164(contact.primary_phone, contact.primary_phone_country_code || '+1')
    : null;

  // Last call info
  const lastCallDaysAgo = item.last_call_at
    ? differenceInDays(new Date(), new Date(item.last_call_at))
    : null;
  const lastCallAgo = item.last_call_at
    ? formatDistanceToNow(new Date(item.last_call_at), { locale: ptBR, addSuffix: true })
    : 'Nunca contactado';
  // Warn if contact has a recent call (< 60 days) — shouldn't normally happen
  const hasRecentCall = lastCallDaysAgo !== null && lastCallDaysAgo < 60;

  const handleCall = async () => {
    if (!contact.primary_phone) { toast.error('Contato sem telefone cadastrado'); return; }
    if (contact.do_not_call) { toast.error('Este contato está marcado como "não ligar"'); return; }
    try {
      await makeCall(phoneE164!, contact.id);
      toast.success(`Ligando para ${fullName}...`);
    } catch (err) {
      console.error('[Outbound] Call error:', err);
      toast.error('Erro ao iniciar ligação');
    }
  };

  const handleWhatsApp = () => {
    if (!contact.primary_phone) { toast.error('Contato sem telefone cadastrado'); return; }
    if (contact.do_not_message) { toast.error('Este contato está marcado como "não mensagem"'); return; }
    setShowWhatsApp(true);
  };

  const handleNoAnswer = () => {
    onStatusChange(item.id, item.sdr_id, 'called_no_answer', true);
    toast.success('Registrado: sem resposta');
  };

  const handleNotInterested = () => {
    onStatusChange(item.id, item.sdr_id, 'called_not_interested', false);
    toast.success('Registrado: não quis');
  };

  const handleSaveNote = () => {
    onNoteSave(item.id, item.sdr_id, notesValue);
    setNotesOpen(false);
    toast.success('Nota salva');
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDealCreated = async (dealData: any) => {
    try {
      await createDeal({ ...dealData, contact_id: contact.id });
      onStatusChange(item.id, item.sdr_id, 'deal_created', false);
      setShowDealForm(false);
      toast.success('Deal criado com sucesso!');
    } catch (err) {
      console.error('[Outbound] Create deal error:', err);
      toast.error('Erro ao criar deal');
    }
  };

  return (
    <>
      <div className="rounded-xl border bg-card shadow-sm hover:shadow-md transition-shadow">
        {/* Header row */}
        <div className="flex items-start gap-3 p-4">
          {/* Avatar */}
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary font-semibold text-sm">
            {(contact.first_name?.[0] ?? '?').toUpperCase()}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">{fullName}</span>
              <Badge className={`text-xs px-2 py-0 ${STATUS_COLORS[item.status]}`}>
                {STATUS_LABELS[item.status]}
              </Badge>
              {item.batch_label && (
                <Badge
                  variant="outline"
                  className="text-[10px] px-2 py-0 border-blue-300 text-blue-700 bg-blue-50"
                  title={`Lote: ${item.batch_label}`}
                >
                  {item.batch_label}
                </Badge>
              )}
              {/* Attempts badge — escalates color with count */}
              {item.call_attempts > 0 && (
                <Badge
                  className={`text-xs px-2 py-0 font-semibold flex items-center gap-1 ${
                    item.call_attempts >= 3
                      ? 'bg-red-100 text-red-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}
                >
                  <Phone className="h-2.5 w-2.5" />
                  {item.call_attempts}x
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-3 mt-0.5 flex-wrap text-sm text-muted-foreground">
              {phoneFormatted && <span>{phoneFormatted}</span>}
              {contact.country_of_birth && <span>· {contact.country_of_birth}</span>}
              <span className={`flex items-center gap-1 ${hasRecentCall ? 'text-amber-600 font-medium' : ''}`}>
                {hasRecentCall
                  ? <AlertCircle className="h-3 w-3" />
                  : <Clock className="h-3 w-3" />
                }
                {lastCallAgo}
              </span>
            </div>

            {contact.primary_email && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">{contact.primary_email}</p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1 shrink-0">
            <Button
              size="sm"
              variant="default"
              className="h-8 px-3"
              onClick={handleCall}
              disabled={isUpdating || !contact.primary_phone}
              title="Ligar via Twilio"
            >
              <Phone className="h-4 w-4 mr-1" />
              Ligar
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 text-green-700 border-green-200 hover:bg-green-50"
              onClick={handleWhatsApp}
              disabled={!contact.primary_phone}
              title="WhatsApp"
            >
              <MessageCircle className="h-4 w-4" />
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 text-blue-700 border-blue-200 hover:bg-blue-50"
              onClick={() => setShowDealForm(true)}
              disabled={isUpdating}
              title="Criar Deal"
            >
              <Briefcase className="h-4 w-4" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleNoAnswer} disabled={isUpdating}>
                  <PhoneOff className="h-4 w-4 mr-2 text-yellow-600" />
                  Sem resposta
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleNotInterested} disabled={isUpdating}>
                  <XCircle className="h-4 w-4 mr-2 text-red-500" />
                  Não quis
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setNotesOpen((v) => !v)}>
                  <StickyNote className="h-4 w-4 mr-2" />
                  {notesOpen ? 'Fechar notas' : 'Adicionar nota'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a
                    href={`/ContactDetail?id=${contact.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Ver contato completo
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Notes section */}
        {notesOpen && (
          <div className="px-4 pb-3 border-t pt-3 space-y-2">
            <Textarea
              placeholder="Anotações sobre este contato..."
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              className="text-sm min-h-[80px] resize-none"
              autoFocus
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveNote}>Salvar nota</Button>
              <Button size="sm" variant="ghost" onClick={() => setNotesOpen(false)}>Cancelar</Button>
            </div>
          </div>
        )}

        {/* Existing note preview — click to edit */}
        {item.notes && !notesOpen && (
          <div
            className="px-4 pb-2 text-xs text-muted-foreground italic border-t pt-2 cursor-pointer hover:text-foreground transition-colors"
            onClick={() => setNotesOpen(true)}
            title="Clique para editar"
          >
            {item.notes}
          </div>
        )}

        {/* History toggle */}
        <div className="border-t">
          <button
            onClick={() => setHistoryOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-2 text-xs text-muted-foreground hover:bg-muted/30 transition-colors rounded-b-xl"
          >
            <span className="flex items-center gap-1.5">
              Histórico de ligações
              {hasRecentCall && (
                <span className="text-amber-600 font-medium">· última {lastCallAgo}</span>
              )}
            </span>
            {historyOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>

          {historyOpen && (
            <div className="px-4 pb-4">
              <OutboundCallHistory contactId={contact.id} />
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp Sheet — internal Twilio WhatsApp, same component as ContactDetail */}
      <Sheet open={showWhatsApp} onOpenChange={setShowWhatsApp}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
          <SheetHeader className="px-4 pt-4 pb-2 border-b shrink-0">
            <SheetTitle className="text-base">WhatsApp — {fullName}</SheetTitle>
            {phoneFormatted && (
              <p className="text-xs text-muted-foreground -mt-1">{phoneFormatted}</p>
            )}
          </SheetHeader>
          <div className="flex-1 overflow-hidden">
            <ChatCore
              contactId={contact.id}
              contactPhone={phoneE164 ?? undefined}
              showConversationSelector
              header={
                <ContactChatHeader
                  phoneNumber={phoneE164 ?? undefined}
                  isConnected
                />
              }
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Deal creation dialog */}
      <Dialog open={showDealForm} onOpenChange={setShowDealForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Criar Deal — {fullName}</DialogTitle>
          </DialogHeader>
          <DealForm
            initialContactId={contact.id}
            onSubmit={handleDealCreated}
            onCancel={() => setShowDealForm(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
